import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { STATUS_EQUIPAMENTO, ehStatusEquipamento } from '@/lib/dominio';
import { anunciar } from '@/lib/retorno';

import { useCadastrosDaOperacao } from '@/modulos/operacao/consultas';
import { useSessao } from '@/modulos/sessao/consultas';

import { useMudarSituacao } from './consultas-frota';
import { oQueODetalheLe } from './leitura-do-detalhe';
import { MensagemDeErro } from './MensagemDeErro';
import type { EquipamentoDaFrota } from './frota';

/**
 * Ferramenta 4 — situacao e mobilizacao.
 *
 * ## O motivo nao e opcional, e o botao so liga quando ele esta escrito
 *
 * O Manual da Marca fecha em SEIS status e manda os estados extras do guia de
 * construcao (`Aguardando peca`, `Aguardando operacao`, `Sem contrato`,
 * `Mobilizacao`) entrarem como MOTIVO dentro do status. Se o motivo pudesse
 * ficar em branco, a frota inteira ficaria "Parada" sem ninguem saber por que —
 * e a coluna de motivo da lista, que e onde essa informacao aparece, nasceria
 * vazia. O banco tambem recusa (coluna NOT NULL com `check`); aqui o botao
 * desligado apenas diz isso antes de a pessoa mandar.
 *
 * ## A CAUSA CATALOGADA, e por que ela nao aparece sempre
 *
 * `Parada` e `Em manutencao` exigem uma causa escolhida na lista; os outros
 * quatro status nao. A assimetria vem do v6: a oportunidade do modulo 03 e
 * "atacar causas repetitivas em vez de apenas seus efeitos", e causa repetitiva
 * so existe onde ha o que repetir — texto livre da tres frases para a mesma
 * bomba. E pedir causa de parada para a maquina estar OPERANDO obrigaria a
 * inventar uma.
 *
 * O texto continua obrigatorio nos seis: o catalogo diz a CAUSA ("bomba
 * hidraulica") e o texto diz o CASO ("bomba 3 do ESC-001, vazando pela
 * gaxeta"). Quem ler a trilha em tres meses precisa dos dois.
 *
 * ## O que acontece com a situacao anterior
 *
 * Ela e ENCERRADA no mesmo instante, nao apagada. A frase abaixo do formulario
 * diz isso com todas as letras, porque e a diferenca entre "a maquina esta
 * parada" e "a maquina ficou parada de terca as 14:32 ate agora" — e e a
 * segunda que responde pergunta de gestor.
 */
/** Os dois status onde a disponibilidade se perde — e onde a causa e cobrada. */
const STATUS_QUE_EXIGEM_CAUSA = ['Parada', 'Em manutenção'];

export function FormularioSituacao({ equipamento }: { readonly equipamento: EquipamentoDaFrota }) {
  const base = useId();
  const [status, setStatus] = useState<string>(equipamento.status ?? 'Disponível');
  const [motivo, setMotivo] = useState('');
  const [motivoId, setMotivoId] = useState('');
  const mudar = useMudarSituacao();
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? null;
  const registra = (permissoes ?? []).includes('situacao.registrar');
  /* AS CAUSAS SÓ SAEM PARA QUEM REGISTRA SITUAÇÃO E LÊ A OPERAÇÃO (robôs,
     rodada 2): almoxarifado e compras abriam a máquina e o painel pedia
     `/operacao/cadastros` — 403 e toast — para um formulário que nem aparece
     para eles. */
  const leCausas = oQueODetalheLe(permissoes).causas;
  const cadastros = useCadastrosDaOperacao(registra && leCausas);

  const exigeCausa = STATUS_QUE_EXIGEM_CAUSA.includes(status);
  const causas = cadastros.data?.motivos ?? [];

  /* TRES SITUACOES DIFERENTES, e a tela nao pode trata-las como uma so:
     carregando, sem permissao para ler as causas, e catalogo vazio. A terceira
     e um beco — a pessoa preenche tudo e o botao nao funciona —, entao ela e
     dita ANTES, com o caminho de saida. */
  /* Sem `operacao.ler` a consulta nem sai — e consulta desligada fica
     "pendente" para sempre: aqui ela conta como inalcançável, não carregando. */
  const causasCarregando = exigeCausa && leCausas && cadastros.isPending;
  const causasInalcancaveis = exigeCausa && (!leCausas || cadastros.isError);
  const semCausaCadastrada = exigeCausa && leCausas && !cadastros.isPending && !cadastros.isError && causas.length === 0;

  const motivoLimpo = motivo.trim();
  const podeGravar =
    ehStatusEquipamento(status) &&
    motivoLimpo.length > 0 &&
    (!exigeCausa || motivoId !== '') &&
    !mudar.isPending;

  /*
   * O QUE FALTA PARA REGISTRAR, ESCRITO AO LADO DO BOTÃO (26/10/2026).
   *
   * Você mandou o print do botão apagado com a legenda "botão salvar", e logo
   * depois a lista sem a mudança, com "não está refletindo". É a mesma causa,
   * e não duas: o Motivo estava vazio — o texto cinza ali é o EXEMPLO, não um
   * valor —, o botão nunca ligou, nada foi gravado, e por isso a lista não
   * tinha o que mostrar.
   *
   * O erro do campo só aparece depois de sair dele (é a regra do manual: o
   * sistema que grita no segundo caractere ensina a ignorar mensagem de erro).
   * Quem nunca encostou no campo via, então, um botão morto e nenhuma
   * explicação — o beco que o comentário das causas, logo acima, já descrevia
   * para o catálogo vazio e que existia igual aqui.
   *
   * A frase fica ao lado do botão, e não no lugar do erro do campo: ela
   * responde "por que não consigo clicar", que é outra pergunta.
   */
  const oQueFalta = ((): string | null => {
    if (mudar.isPending) return null;
    if (!ehStatusEquipamento(status)) return 'Escolha um dos status oficiais.';
    if (exigeCausa && motivoId === '') return 'Escolha a causa da parada para registrar.';
    if (motivoLimpo.length === 0) return 'Escreva o motivo da mudança para registrar.';
    return null;
  })();

  /* Só quem registra situação vê o formulário (prova do marco, 07/10/2026):
     quem só lê a máquina vê o histórico de situações, abaixo. */
  if (!registra) return null;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!podeGravar) return;
        mudar.mutate(
          {
            equipamentoId: equipamento.id,
            status,
            motivo: motivoLimpo,
            motivoId: exigeCausa ? motivoId : null,
            /* A situacao que a tela MOSTRA agora (robos de 20/10/2026, achado
               10): se outra pessoa mudou depois que a ficha foi lida, o
               servidor recusa, a ficha rele e mostra a situacao de agora. */
            situacaoVista: equipamento.situacaoId,
          },
          {
            onSuccess: () => {
              /* Retorno de ação é toast (p.30, p.31), e não uma frase no pé do
                 formulário que fica lá depois de a pessoa mudar de assunto. */
              anunciar({
                tom: 'sucesso',
                titulo: 'Situação registrada',
                alvo: { codigo: equipamento.codigo, nome: equipamento.descricao },
                fatos: [motivoLimpo],
                efeito: { rotulo: 'Status', antes: equipamento.status ?? 'Sem status', depois: status },
              });
              setMotivo('');
              setMotivoId('');
            },
          },
        );
      }}
    >
      {/* Os campos na grade do painel (pedido do dono, 31/10/2026): lado a
          lado na janela larga, um embaixo do outro no painel estreito. */}
      <div className="grade-do-painel">
      <CampoSelecao
        rotulo="Novo status"
        valor={status}
        aoMudar={setStatus}
        opcoes={STATUS_EQUIPAMENTO.map((nome) => ({ valor: nome, rotulo: nome }))}
      />

      {!exigeCausa ? null : causasCarregando ? (
        /* As causas ainda vem (p.32): o rotulo fica, e so o controle vira a
           barra de 40 px. */
        <Campo id={`${base}-causa`} rotulo="Causa da parada">
          <ControleCarregando oQue="Carregando as causas de parada" />
        </Campo>
      ) : causasInalcancaveis ? (
        <p className="campo-largo txt-apoio text-tinta-secundaria">
          Seu perfil não vê as causas de parada, e sem elas não dá para registrar parada nem
          manutenção. Peça a liberação ao administrador da empresa.
        </p>
      ) : semCausaCadastrada ? (
        <p className="campo-largo txt-apoio text-tinta-secundaria">
          Nenhuma causa de parada cadastrada. Cadastre as causas em Operação → Causas de parada
          antes de registrar uma parada: é o agrupamento por causa que mostra o que mais tira
          disponibilidade.
        </p>
      ) : (
        <CampoSelecao
          rotulo="Causa da parada"
          valor={motivoId}
          aoMudar={setMotivoId}
          rotuloVazio="Escolha a causa"
          opcoes={causas.map((c) => ({
            valor: c.id,
            /* A PALAVRA "evitável" no rótulo, e não um sinal: quem escolhe a
               causa é quem sabe se ela era evitável, e ver isso na hora da
               escolha é o que mantém o catálogo honesto. */
            rotulo: `${c.nome} (${c.evitavel ? 'evitável' : 'não evitável'})`,
          }))}
        />
      )}

      <div className="campo-largo">
        <CampoTexto
          rotulo="Motivo"
          valor={motivo}
          aoMudar={setMotivo}
          placeholder="Aguardando peça, bomba hidráulica"
          ajuda="Toda mudança de status grava o motivo. Ele aparece na lista, ao lado do status. A causa agrupa; este texto descreve o caso."
          validar={(valor) =>
            valor.trim().length > 0 ? null : 'Escreva o motivo da mudança para poder registrar.'
          }
        />
      </div>
      </div>

      <MensagemDeErro erro={mudar.error} />

      {/* Acoes ao fim do formulario, e nao na `barra-de-acoes` fixa: o painel
          de detalhe ja tem rodape fixo proprio (R01, p.29). */}
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-borda-sutil pt-3">
        {oQueFalta ? (
          <span className="txt-apoio mr-auto text-tinta-secundaria">{oQueFalta}</span>
        ) : null}
        <Botao type="submit" nivel="secundario" disabled={!podeGravar}>
          {mudar.isPending ? 'Registrando' : 'Registrar situação'}
        </Botao>
      </div>

      <p className="txt-apoio text-tinta-apagada">
        {equipamento.status
          ? `A situação atual (${equipamento.status}) é encerrada neste instante e fica no histórico. Nada é apagado.`
          : 'Esta máquina ainda não tem situação registrada. Esta será a primeira do histórico.'}
      </p>
    </form>
  );
}
